import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { Smartphone, Activity, Moon, Flame, RefreshCw, ChevronRight, Apple, ChevronDown, ChevronUp } from 'lucide-react';
import { Language } from '../types';

interface GoogleFitWidgetProps {
  isConnected: boolean;
  isAppleConnected?: boolean;
  appleCalories?: number | null;
  googleFitData: any[];
  googleFitLoading: boolean;
  onSync: () => void;
  lang: Language;
  illumination: boolean;
  onConnectClick?: () => void;
  defaultOpen?: boolean;
}

export const GoogleFitWidget: React.FC<GoogleFitWidgetProps> = ({
  isConnected,
  isAppleConnected = false,
  appleCalories = null,
  googleFitData,
  googleFitLoading,
  onSync,
  lang,
  illumination,
  onConnectClick,
  defaultOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const isUa = lang === 'ua';
  const showAppleOnly = isAppleConnected && !isConnected;

  const t = {
    title: showAppleOnly 
      ? (isUa ? 'Apple Health Активність' : 'Apple Health Activity')
      : (isUa ? 'Google Fit Активність' : 'Google Fit Activity'),
    connected: showAppleOnly
      ? (isUa ? 'Apple Health Підключено' : 'Apple Health Connected')
      : (isUa ? 'Google Fit Підключено' : 'Google Fit Connected'),
    disconnected: showAppleOnly
      ? (isUa ? 'Apple Health Непідключено' : 'Apple Health Disconnected')
      : (isUa ? 'Google Fit Непідключено' : 'Google Fit Disconnected'),
    connectDesc: isUa 
      ? 'Підключіть Google Fit, щоб автоматично синхронізувати кроки, сон та спалені калорії з вашим профілем.' 
      : 'Connect Google Fit to automatically sync your daily steps, sleep, and active calories.',
    connectBtn: isUa ? 'Підключити Google Fit' : 'Connect Google Fit',
    todayTitle: isUa ? 'Дані за сьогодні' : "Today's Activity",
    steps: isUa ? 'Кроки' : 'Steps',
    sleep: isUa ? 'Сон' : 'Sleep',
    activeCals: isUa ? 'Спалені калорії' : 'Active Burn',
    syncBtn: isUa ? 'Оновити' : 'Sync Now',
    syncing: isUa ? 'Синхронізація...' : 'Syncing...',
    of: isUa ? 'з' : 'of',
    hours: isUa ? 'год' : 'hrs',
    kcal: isUa ? 'ккал' : 'kcal',
    goals: {
      steps: 10000,
      sleep: 8,
      calories: 400
    }
  };

  // Extract today's data (last element in the 7-day array is always the most recent)
  const todayData = googleFitData && googleFitData.length > 0 ? googleFitData[googleFitData.length - 1] : null;

  // Use values from Google Fit if available, else standard fallback
  const steps = todayData?.steps ?? 0;
  const sleep = todayData?.sleep ?? 0;
  // If calories is returned in aggregate, use it, else let's approximate active burn from steps
  const activeCalories = todayData?.calories 
    ? Math.max(0, todayData.calories - 2000) // subtract BMR approximation to get active burn, or just show active
    : Math.round(steps * 0.04); // rough approximation: 0.04 kcal per step

  const stepsPct = Math.min(100, (steps / t.goals.steps) * 100);
  const sleepPct = Math.min(100, (sleep / t.goals.sleep) * 100);
  const calsPct = Math.min(100, (activeCalories / t.goals.calories) * 100);

  const finalIsConnected = isConnected || isAppleConnected;

  return (
    <div className={cn(
      "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 relative overflow-hidden card-shadow transition-all",
      illumination ? "illumination-both" : ""
    )}>
      {illumination && (
        <>
          <div className="absolute -left-20 -top-20 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none" />
          <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-teal-500/5 rounded-full blur-[80px] pointer-events-none" />
        </>
      )}

      {/* Header section with connection indicator */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between relative z-10 cursor-pointer select-none group",
          isOpen ? "mb-5" : "mb-0"
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2.5 rounded-2xl flex items-center justify-center transition-colors",
            finalIsConnected ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 border border-transparent"
          )}>
            {showAppleOnly ? <Apple className="w-5 h-5 text-emerald-500" /> : <Smartphone className="w-5 h-5" />}
          </div>
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest text-[var(--text)]">
              {t.title}
            </h4>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={cn(
                "w-2 h-2 rounded-full",
                finalIsConnected ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"
              )} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                {finalIsConnected ? t.connected : t.disconnected}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isConnected && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSync();
              }}
              disabled={googleFitLoading}
              className={cn(
                "p-2 rounded-xl bg-[var(--input-bg)] text-zinc-500 hover:text-[var(--text)] transition-colors flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider disabled:opacity-50 border border-[var(--card-border)] hover:border-emerald-500/30",
                googleFitLoading && "pointer-events-none"
              )}
              title={t.syncBtn}
            >
              <RefreshCw className={cn("w-3.5 h-3.5 text-emerald-500", googleFitLoading && "animate-spin")} />
              <span className="hidden sm:inline">{googleFitLoading ? t.syncing : t.syncBtn}</span>
            </button>
          )}

          <div className="p-1.5 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors">
            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="relative z-10 overflow-hidden"
          >
            {!finalIsConnected ? (
              <div className="space-y-4 text-center py-2">
                <p className="text-xs text-zinc-500 leading-relaxed max-w-[90%] mx-auto">
                  {t.connectDesc}
                </p>
                <button
                  onClick={onConnectClick}
                  className="mx-auto flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 active:scale-95 hover:bg-emerald-600 transition-all text-center"
                >
                  <Smartphone className="w-4 h-4" />
                  {t.connectBtn}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : showAppleOnly ? (
              <div className="space-y-4">
                <div className="bg-[var(--input-bg)] p-4 rounded-2xl border border-[var(--card-border)] flex items-center gap-4">
                  <div className="p-2.5 bg-emerald-500/10 text-emerald-500 rounded-xl border border-emerald-500/10 shrink-0">
                    <Apple className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-bold text-[var(--text)] uppercase tracking-wider">
                        {isUa ? 'Спалені калорії' : 'Active Burn'}
                      </span>
                      <div className="text-xs font-mono font-black text-emerald-500">
                        {appleCalories ?? 0} <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-tight">{t.kcal}</span>
                      </div>
                    </div>
                    <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2 mt-2 overflow-hidden">
                      <div 
                        className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, ((appleCalories ?? 0) / t.goals.calories) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Steps Track */}
                <div className="bg-[var(--input-bg)] p-3 rounded-2xl border border-[var(--card-border)] flex items-center gap-4">
                  <div className="p-2 bg-emerald-500/10 text-emerald-500 rounded-xl border border-emerald-500/10 shrink-0">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-bold text-[var(--text)] uppercase tracking-wider">{t.steps}</span>
                      <div className="text-xs font-mono font-black text-emerald-500">
                        {steps.toLocaleString()} <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-tight">{t.of} {t.goals.steps.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2 mt-2 overflow-hidden">
                      <div 
                        className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${stepsPct}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Split row for Sleep and Active Burn */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Sleep Track */}
                  <div className="bg-[var(--input-bg)] p-3 rounded-2xl border border-[var(--card-border)] flex flex-col justify-between min-h-[90px]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">{t.sleep}</span>
                      <Moon className="w-4 h-4 text-indigo-400" />
                    </div>
                    <div>
                      <div className="text-lg font-mono font-black text-indigo-500">
                        {sleep} <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{t.hours}</span>
                      </div>
                      <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-1 mt-2 overflow-hidden">
                        <div 
                          className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${sleepPct}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Calories Expended Track */}
                  <div className="bg-[var(--input-bg)] p-3 rounded-2xl border border-[var(--card-border)] flex flex-col justify-between min-h-[90px]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">{t.activeCals}</span>
                      <Flame className="w-4 h-4 text-amber-500" />
                    </div>
                    <div>
                      <div className="text-lg font-mono font-black text-amber-500">
                        {activeCalories} <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{t.kcal}</span>
                      </div>
                      <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-1 mt-2 overflow-hidden">
                        <div 
                          className="bg-amber-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${calsPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

